/**
 * Catalog explorer data: Category → Course → Chapter → Topic → Subtopic, from GET /catalog/tree.
 *
 *   const catalog = useCatalogTree({ api, counts: 'questions' });
 *   catalog.nodes           explorer nodes (see ExplorerTree)
 *   catalog.reload()
 *   catalog.byKey(key)      → { kind, category, course, node, descendants }
 *
 * Node keys: cat:<key> · course:<id> · node:<id>
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Folder, FolderOpen, Books, BookOpen, Bookmarks, File, Exam, FileArrowUp, Question } from '@phosphor-icons/react';

export const NODE_KIND_LABEL = { section: 'Chapter', subsection: 'Topic', content: 'Subtopic', file: 'Subtopic (file)', exam: 'Exam' };

function iconFor(n) {
  if (n.node_type === 'section') return [BookOpen, 'text-amber-600'];
  if (n.node_type === 'subsection') return [Bookmarks, 'text-sky-600'];
  if (n.node_type === 'exam') return [Exam, 'text-violet-600'];
  if (n.node_type === 'file') return [FileArrowUp, 'text-slate-500'];
  return [File, 'text-slate-500'];
}

export function buildCourseNodeTree(nodes, courseKeyPrefix, withCounts, containersOnly = false) {
  // descendants from the FULL list (so a chapter still covers questions filed under hidden subtopics)
  const fullByParent = {};
  nodes.forEach((n) => { (fullByParent[n.parent_id || '__root'] = fullByParent[n.parent_id || '__root'] || []).push(n); });
  const descendantIds = (id) => (fullByParent[id] || []).flatMap((c) => [c.id, ...descendantIds(c.id)]);
  const byParent = {};
  (containersOnly ? nodes.filter((n) => n.node_type === 'section' || n.node_type === 'subsection') : nodes).forEach((n) => { (byParent[n.parent_id || '__root'] = byParent[n.parent_id || '__root'] || []).push(n); });
  const build = (parentId) => (byParent[parentId || '__root'] || [])
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
    .map((n) => {
      const [icon, iconClass] = iconFor(n);
      return {
        key: `node:${n.id}`, label: n.title, icon, iconClass, kind: 'node', data: n, descendantIds: descendantIds(n.id),
        title: `${NODE_KIND_LABEL[n.node_type] || n.node_type}: ${n.title}`,
        badge: withCounts ? (n.question_count || undefined) : undefined,
        children: build(n.id),
      };
    });
  return build(null);
}

export default function useCatalogTree({ api, counts = null, enabled = true, containersOnly = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await api.get(`/catalog/tree${counts ? `?counts=${counts}` : ''}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      // A failed load is NOT an empty catalogue. Standing in a placeholder object here
      // is what rendered "undefined courses · undefined chapters" in the tree footer,
      // and told the user they had no courses when the request had simply failed.
      setError(e?.response?.data?.detail || 'Could not load the course tree');
    } finally {
      setLoading(false);
    }
  }, [api, counts, enabled]);
  useEffect(() => { reload(); }, [reload]);

  const withCounts = counts === 'questions';
  const nodes = useMemo(() => {
    const cats = data?.categories || [];
    const courseNode = (c) => ({
      key: `course:${c.id}`, label: c.title, icon: Books, iconClass: c.is_published ? 'text-shell-primary' : 'text-muted-foreground', kind: 'course', data: c, muted: !c.is_published,
      title: `${c.title}${c.course_code ? ` (${c.course_code})` : ''}${c.is_published ? '' : ' — draft'}`,
      badge: withCounts ? (c.question_count || undefined) : undefined,
      children: buildCourseNodeTree(c.nodes || [], `course:${c.id}`, withCounts, containersOnly),
    });
    // categories nest to any depth: sub-categories first, then the category's own courses
    const catNode = (cat) => ({
      key: `cat:${cat.key}`, label: cat.label, icon: cat.children?.length ? FolderOpen : Folder, iconClass: 'text-amber-500', kind: 'category', data: cat,
      title: cat.path && cat.path !== cat.label ? cat.path : undefined,
      badge: withCounts ? (cat.question_count || undefined) : (cat.course_count || undefined),
      children: [...(cat.children || []).map(catNode), ...(cat.courses || []).map(courseNode)],
    });
    const out = cats.map(catNode);
    if (withCounts && data?.unfiled_question_count) {
      out.push({ key: 'unfiled', label: 'Not linked to a course', icon: Question, iconClass: 'text-muted-foreground', kind: 'unfiled', badge: data.unfiled_question_count, children: [], muted: true });
    }
    return out;
  }, [data, withCounts, containersOnly]);

  const index = useMemo(() => {
    const map = {};
    const walk = (list, cat, course) => list.forEach((n) => {
      const entry = { node: n, kind: n.kind, category: cat, course };
      map[n.key] = entry;
      if (n.kind === 'category') walk(n.children, n, null); // sub-categories re-point `category` to themselves
      else if (n.kind === 'course') walk(n.children, cat, n);
      else walk(n.children, cat, course);
    });
    walk(nodes, null, null);
    return map;
  }, [nodes]);

  const byKey = useCallback((key) => {
    const e = index[key];
    if (!e) return null;
    const descendants = [];
    const walk = (n) => n.children?.forEach((c) => { descendants.push(c); walk(c); });
    walk(e.node);
    return { ...e, descendants };
  }, [index]);

  return { nodes, data, loading, error, reload, byKey };
}
